import axios, { AxiosRequestConfig } from "axios";
import { getUserInfo } from "@/utils/oidcUtil";

const UNAUTHORIZED_STATUS_CODE = 401;
const REFETCH_INTERVAL = 30;
const OK_STATUS = 200;
const CREATED_STATUS = 201;
const ACCEPTED_STATUS = 202;
const NO_CONTENT_STATUS = 204;

type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

const setSessionExpiredHandler = (handler: SessionExpiredHandler) => {
    onSessionExpired = handler;
};

const waitForToken = () => {
    return new Promise(resolve => {
        const interval = setInterval(() => {
            const token = getUserInfo()?.access_token;
            if (token) {
                clearInterval(interval);
                resolve(token);
            }
        }, REFETCH_INTERVAL);
    });
};

const axiosInstance = async () => {
    const token = (await waitForToken()) as string;
    const axiosInst = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        }
        /* other custom settings */
    });
    axiosInst.interceptors.request.use(config => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    });
    // --- Global 401 handler ---
    axiosInst.interceptors.response.use(
        response => response,
        error => {
            if (error?.response?.status === UNAUTHORIZED_STATUS_CODE && typeof onSessionExpired === "function") {
                onSessionExpired();
            }
            return Promise.reject(error);
        }
    );
    return axiosInst;
};

const instance = axiosInstance();

const httpGet = async <T>(url: string, requestConfig?: AxiosRequestConfig): Promise<T> => {
    const apiClient = await instance;
    try {
        const response = await apiClient.get(url, requestConfig);

        if (response.status !== OK_STATUS) {
            throw new Error(`API call to ${url} failed with status ${response.status}`);
        }

        return response.data;
    } catch (error) {
        throw error instanceof Error ? error : new Error(`API call to ${url} failed`, { cause: error });
    }
};
const httpPost = async <T, I>(url: string, data: I, requestConfig?: AxiosRequestConfig): Promise<T> => {
    const apiClient = await instance;
    try {
        const response = await apiClient.post(url, data, requestConfig);

        if (
            response.status !== OK_STATUS &&
            response.status !== CREATED_STATUS &&
            response.status !== ACCEPTED_STATUS &&
            response.status !== NO_CONTENT_STATUS
        ) {
            throw new Error(`API call to ${url} failed with status ${response.status}`);
        }
        return response.data;
    } catch (error) {
        throw error instanceof Error ? error : new Error(`API call to ${url} failed`, { cause: error });
    }
};

const httpUpdate = async <T, I>(url: string, data: I, requestConfig?: AxiosRequestConfig): Promise<T> => {
    const apiClient = await instance;
    try {
        const response = await apiClient.put(url, data, requestConfig);
        if (response.status !== OK_STATUS) {
            throw new Error(`API call to ${url} failed with status ${response.status}`);
        }
        return response.data;
    } catch (error) {
        throw error instanceof Error ? error : new Error(`API call to ${url} failed`, { cause: error });
    }
};

const httpDelete = async <T>(url: string, requestConfig?: AxiosRequestConfig): Promise<T> => {
    const apiClient = await instance;
    try {
        const response = await apiClient.delete(url, requestConfig);

        if (response.status !== OK_STATUS) {
            throw new Error(`API call to ${url} failed with status ${response.status}`);
        }
        return response.data;
    } catch (error) {
        throw error instanceof Error ? error : new Error(`API call to ${url} failed`, { cause: error });
    }
};

export { httpGet, httpDelete, httpPost, httpUpdate };
export { setSessionExpiredHandler };

export type QueryFilter = {
    query?: string;
    page: number;
    limit: number;
    sortDirection: "ASC" | "DESC";
    sortBy?: string;
};

export type SearchResultDto<T> = {
    items: T[];
    returnedResults: number;
    page: number; // 1-based from server
    totalResults: number;
};

export type NormalizedSearchResultDto<T> = {
    items: T[];
    returnedResults: number;
    pageIndex: number; // 0-based for UI
    totalResults: number;
};

export type ResultDto<T> = {
    resultCode: string;
    description: string;
    resultData: T;
};
